import _ from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { faPencilAlt, faPlus, faTrashAlt, faFileAlt, faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SecondaryButton from '../../../../ui/SecondaryButton';
import IfmsModal from '../../../../ui/IfmsModal';
import useTableNavigation from '../../../../ui/tables/useTableNavigation';
import { accountCodeList, ApplicableTableSettings } from '../../../../tableSettings';

import {
  TextAlignLeftRenderer,
  TextRenderer,
  NumberedIndicatorRenderer,
  NullableCheckboxRedIconRenderer,
  DateRenderer,
} from '../../../../uiControls/SmartTableRenderers';

import SmartTable from '../../../../uiControls/SmartTable';
import ApplicationContext from '../../../../ApplicationContext';
import useGlobalYesNoDialog from '../../../../hooks/useGlobalYesNoDialog';
import useGlobalOkDialog from '../../../../hooks/useGlobalOkDialog';
import useFastStorage, { useTypedFastStorage } from '../../../../hooks/useFastStorage';
import useFunction from '../../../../hooks/useFunction';
import { JobNames } from '../../../../framework.core/JobNames';
import { CodeAccountListItem } from '../../../../fms.data/models/CodeAccountListItem';
import { initialSearch, isFilterObjectEmpty, Search } from '../../../../filtering';
import { CodeGlNo } from '../../../../fms.core/CodeGlNo';
import useDirtyCheck from '../../../../hooks/useDirtyCheck2';
import { ApiResult } from '../../../../framework.core/data/ApiResult';
import { FmsSecTask } from '../../../../fms.core/constants';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';
import { sortingListData } from '../../ReferenceFiles';
import { findSecurityTask } from '../../../../util';
import useFilterBar from '../../../../hooks/useFilterBar';
import { ListOrderBy } from "../../../common/AbstractListView";
import postal from 'postal';
import BlackInverseButton from '../../../../ui/BlackInverseButton';
import constants from '../../../../constants';
import AccountCodeDetails from './AccountCodeDetails';
import IFmsReferenceFilesContext from '../../IfmsReferenceFilesContext';
import { Portal } from 'react-portal';
import CloseButton from '../../../../ui/CloseButton';
import { exportCodeDataIntoExcel } from '../../../../excel';
import { formatDate, now } from '../../../../dateUtils';
import { getFmsGlobal } from '../../../../globals';

const Groups = [
  { label: '1  ASSET', value: 'A', glNo: '1' },
  { label: '2  LIABILITY', value: 'L', glNo: '2' },
  { label: '3  EQUITY', value: 'Q', glNo: '3' },
  { label: '4  EXPENSE', value: 'E', glNo: '4' },
  { label: '5  SALES', value: 'I', glNo: '5', forFront: 'SALES' },
  { label: '6  COST', value: 'C', glNo: '6', forFront: 'COST' },
  // <chuck date="2024-04-01" desc="NON-OP EXPENSE/INCOME 에 대해 기존 숫자 체계는 유지하되 별도 Group으로 분리하는 작업">
  { label: '45 NON-OP EXPENSE', value: 'N', glNo: '4' },
  { label: '55 NON-OP INCOME', value: 'M', glNo: '5' },
  // </chuck>
];

let SubGroups1: Array<any> = [];

function formatElement(data: any, index: number) {
  return { ...data, indicator: index + 1 };
}

function formatData(data: any) {
  return data.map((d: any, i: number) => formatElement(d, i));
}

function AccountCode({ 
  componentIndex
}: {
  componentIndex: number
}) {
  useFunctionKeys({ onEsc: cancelOperation });

  const appContext = useContext(ApplicationContext);
  const refContext = useContext(IFmsReferenceFilesContext);
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const { selectTableRow, tableNavigationCleanup } = useTableNavigation('account-code-list', onRowSelect, selectAccountCodeForView, ['accountCode'], false, false, false);

  const tableRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [, setHasChanges] = useFastStorage(`ifms-referenceFiles-haschanges`, false);
  const [data, _setData, removeData, dataRef] = useFastStorage(`ifms-referenceFiles-accountcode-list`, []);
  const [selectedRow, setSelectedRow] = useState(-1);

  const [accountCode, _setAccountCode, removeAccountCodeFromStorage, accountCodeRef] = useTypedFastStorage<CodeGlNo>(`ifms-referenceFiles-accountcode-editable`, null);
  const { setCleanState } = useDirtyCheck(`ifms-referenceFiles-accountcode-editable`, ['edit', 'view']);
  const [orderBy, setOrderBy] = useFastStorage(`ifms-referenceFiles-accountcode-orderby`, {
    orderBy: 'accountCode',
    orderByAscending: true,
    // general1: 'AccountCode',
    // pageSize: systemSettings.defaultPageSize,
  });
  const listSec = findSecurityTask(appContext, FmsSecTask.CodeAccountCode);
  const tableHeight = 520;

  const fmsGlobal = getFmsGlobal();

  const { FilterButton, search } = useFilterBar(
    accountCodeList,
    undefined,
    getDefaultOrderBy,
    String(componentIndex)
  );

  function getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'accountCode',
      orderByAscending: true,
    };
  }

  function setData(d: any) {
    removeData();
    _setData(d);
  }

  function setAccountCode(d: any, skipDirty: boolean = false) {
    if (!skipDirty) {
      setHasChanges(true);
    }
    _setAccountCode(d)
  }


  async function fetchData(api: any, search: Search) {
    setLoading(true);

    const apiResult = await api.getListItems(JobNames.GLNo,
      'CodeAccountListItem',
      CodeAccountListItem.calculateSearchParameters(search),
      1, search.pageSize);

    const fetchedTableData  = formatData(apiResult && apiResult.entities ? apiResult.entities : []);
    sortingListData(CodeAccountListItem, fetchedTableData , setData, orderBy);
    setHasChanges(false);
    setLoading(false);
  }

  useEffect(() => {
    if (isFilterObjectEmpty(search) && search.version === 1) reload(false);

    return () => {
      removeData();
      tableNavigationCleanup();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (loading || (isFilterObjectEmpty(search) && search.version === 1)) {
      return;
    }
    fetchData(appContext.api, search);
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search]
  );

  useEffect(() => {
    if (dataRef.current?.length > 0 && !loading) {
      selectTableRow(0, true);
      onRowSelect(0);
    }
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading]
  );

  async function reload(clearAll: boolean = true) {
    const searchValue = clearAll ? initialSearch : search;
    await fetchData(appContext.api, searchValue);
  }

  function onRowSelect(rowIndex: number) {
    setSelectedRow(!isNaN(rowIndex) ? rowIndex : -1);
  }

  function onHeaderClick(header: any) {
    if (header && header.id !== 'indicator') {
      if (header.id === orderBy.orderBy) {
        setOrderBy({ orderByAscending: !orderBy.orderByAscending });
      } else {
        setOrderBy({ orderBy: header.id, orderByAscending: true });
      }
    }
    sortingListData(CodeAccountListItem, data, setData, orderBy);
  }

  async function selectAccountCodeForNew() {
    const newModel = new CodeGlNo();
    newModel.edit = false;
    newModel.view = false;
    setAccountCode(newModel, false);
    setCleanState(newModel);

    if (accountCodeRef.current) {
      accountCodeRef.current.trackable = true;
      setAccountCode({
        edit: false,
        view: false,
      });
    }
  }
  async function selectAccountCodeForView(row: number) {
    await setAccountCodeDetail(true, row);
  }
  async function selectAccountCodeForEdit() {
    await setAccountCodeDetail(false);
  }
  async function selectAccountCodeForDelete() {
    const doConfirm = await showGlobalYesNoDialog(
      <div>
        <h3>Confirm to Delete?</h3>
      </div>
    , false, 0, true, 'warning');
    if (doConfirm) {
      const apiResult = await appContext.api.deleteJobDetail(JobNames.GLNo, { id: dataRef.current[selectedRow].id, pimsLinkId: 0 });
      if (apiResult && apiResult.success) {
        await reload(false);
      } else if (apiResult && apiResult.message) {
        showGlobalOkDialog(<p>{apiResult.message}</p>);
      } else
        showGlobalOkDialog(<p>{"Cannot delete"}</p>);
    } else
      return;
  }


  async function exportExcel() {
    const apiResult = await appContext.api.getListItems(JobNames.GLNo,
      'CodeAccountListItem',
      CodeAccountListItem.calculateSearchParameters(initialSearch),
      1, 0);

    exportCodeDataIntoExcel(apiResult.entities ?? [], `AcctCode-${fmsGlobal.AcmeID}`, CodeAccountListItem.getExportCollection());
  }


  async function setAccountCodeDetail(isView: boolean, row: number = -1) {
    const tempRow = row > -1 ? row : selectedRow;  // row double click시 row 값에 rowindex가 들어옴. 이를 우선시 해서 사용함.

    if (tempRow >= 0 && tempRow < dataRef.current.length && _.isEmpty(accountCode)) {
      const apiResult: ApiResult<CodeGlNo> = await appContext.api.getJobDetail(JobNames.GLNo,
        { id: dataRef.current[tempRow].id });
      if (!apiResult || !apiResult.success) {
        await showGlobalOkDialog(<p>{apiResult?.message ?? constants.LOAD_ERROR_MESSAGE}</p>);
        return null;
      } else {
        const accountCode = { ...apiResult.result, edit: true, view: isView, editIndex: tempRow };
        readAccountCode(accountCode);

        //setCleanState(accountCode);
        const tEntity = new CodeGlNo(accountCode);
        setCleanState(tEntity);
        setAccountCode(tEntity, true);
        tEntity.trackable = true;
      }
    }
  }

  function readAccountCode(accountCode: any) {
    let frontDesc = getFrontDesc(accountCode, true);

    let otherFrontDesc = accountCode.glType === 'I' ? frontDesc.replace('SALES', 'COST') : frontDesc.replace('COST', 'SALES');
    accountCode.frontGlDesc = frontDesc
    accountCode.frontLocalDesc = frontDesc;
    accountCode.displayDesc = accountCode.glDescription.replace(frontDesc, '');
    accountCode.displayLocalDesc = accountCode.localDesc.replace(frontDesc, '');

    accountCode.otherFrontGlDesc = otherFrontDesc
    accountCode.otherFrontLocalDesc = otherFrontDesc;
    accountCode.otherdisplayDesc = accountCode.otherGlDesc?.replace(otherFrontDesc, '');
    accountCode.otherdisplayLocalDesc = accountCode.otherLocalDesc?.replace(otherFrontDesc, '');
  }

  function getFrontDesc(accountCode: CodeGlNo, isInitialize : boolean = false): string {
    if (accountCode.glType !== "I" && accountCode.glType !== "C") return '';

    let frontDesc = '';
    if(isInitialize){
      let splitDesc = accountCode.glDescription.split('.');
      frontDesc = splitDesc[0] + '.' + splitDesc[1] + '.' + splitDesc[2] + '.';
    }
    else{
      let desc = Groups?.find(x => x.value === accountCode.glType)?.forFront ?? '';
      if (desc) frontDesc += desc + '.';
      desc = SubGroups1?.find(x => x.value === accountCode.glGroup)?.extraCode ?? '';
      if (desc) frontDesc += desc + '.';
      if (accountCode.glSubGroup) frontDesc += accountCode.glSubGroup + '.';
    }
    return frontDesc;
  }

  function cancelOperation() {
    const filterNode = document.getElementById('ifms-list-screen-filter');
    const channel = postal.channel('ifms-filter');

    if (!filterNode) {
      setHasChanges(false);
      removeAccountCodeFromStorage();
    } else {
      channel.publish('ifms-filter.dismiss');
    }
    
    return true;
  }

  const accountCodeTableOptions: ApplicableTableSettings = {
    customTableId: 'account-code-list',
    ref: tableRef,
    selectable: true,
    tableName: 'accountCodeList',
    ...accountCodeList,
    height: `${tableHeight}px`,
    data: data.map((x: any) => ({ ...x })),
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      { accessor: 'accountCode', Cell: TextRenderer },
      { accessor: 'accountName', Cell: TextAlignLeftRenderer },
      { accessor: 'glType', Cell: TextRenderer },
      { accessor: 'subGroup1', Cell: TextAlignLeftRenderer },
      { accessor: 'subGroup2', Cell: TextAlignLeftRenderer },
      { accessor: 'iocalDesc', Cell: TextAlignLeftRenderer },
      { accessor: 'inactive', Cell: NullableCheckboxRedIconRenderer },
      { accessor: 'inactiveDate', Cell: DateRenderer },
      { accessor: 'remark', Cell: TextAlignLeftRenderer },
    ],
    orderBy: [orderBy],
    onHeaderClick,
  };

  const checkSuntek = appContext.suntekSettings?.company?.companyId === "FSSUNTEK" || appContext.user?.isSuntekAdmin ? true : false;

  const hasSelectedRow = !isNaN(selectedRow) && !(selectedRow < 0);
  const canBeAdd = listSec?.grantAdd;
  const canBeEdit = hasSelectedRow && listSec?.grantEdit;
  const canBeDeleted = hasSelectedRow && listSec?.grantDel && (checkSuntek ||(!!dataRef?.current && !dataRef?.current[selectedRow]?.isDefault));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const RenderedTable = React.useMemo(() => <SmartTable {...accountCodeTableOptions} />, [dataRef.current]);

  return (
    <div style={{ marginTop: '40px' }}>
      <Portal node={document && document.getElementById('settings-command-placeholder')}>
        <div style={{display:'flex', marginTop:'5px'}}>
          <div className="ifms-common-bar-element" style={{marginLeft:'66px'}}>
            <CloseButton name='settings-close' mode='light' onClick={refContext.cancel} />
          </div>
        </div>
      </Portal>
      <div style={{ display: 'flex', width: '1236px' }}>
        <div style={{ margin: 'auto' }}></div>
        <div style={{ position: 'relative', top: '-4px' }}>
          <div style={{ display: 'none' }}>
            <FilterButton /> &nbsp;&nbsp;
          </div>
          <SecondaryButton name='codes-accountcode-add-button' size="regular" disabled={!canBeAdd} onClick={selectAccountCodeForNew}>New</SecondaryButton>
          &nbsp;&nbsp;
          <SecondaryButton name='codes-accountcode-view-button' size="regular" disabled={!hasSelectedRow} onClick={selectAccountCodeForView}>View</SecondaryButton>
          &nbsp;&nbsp;
          <SecondaryButton name='codes-accountcode-edit-button' size="regular" disabled={!canBeEdit} onClick={selectAccountCodeForEdit}>Edit</SecondaryButton>
          &nbsp;&nbsp;
          <SecondaryButton name='codes-accountcode-delete-button' size="regular" disabled={!canBeDeleted} onClick={selectAccountCodeForDelete}>Delete</SecondaryButton>
          &nbsp;
          {checkSuntek &&
            <>
              &nbsp;
              <SecondaryButton name='codes-accountcode-delete-button' size="regular" onClick={exportExcel}>Export</SecondaryButton>
              &nbsp;
            </>
          }
          <BlackInverseButton name='list-screen-refresh' size="large" disabled={loading} onClick={() => { cancelOperation(); reload(false); }} data-tooltip="Refresh" >
            <FontAwesomeIcon color='#47C83E' icon={faSync} />
          </BlackInverseButton>
        </div>
      </div>

      <div style={{ width: '1236px' }}>
        {RenderedTable}
      </div>

      <IfmsModal isOpen={!!accountCode} draggable title={accountCode && accountCode.edit ? (accountCode.view ? 'Account Code View' : 'Account Code Edit') : 'Account Code New'} onCancel={cancelOperation} width='1030px'>
        {!!accountCode &&
          <AccountCodeDetails accountCodeT={accountCode} reload={reload} cancelOperation={cancelOperation} checkSuntek={checkSuntek}/>
        }
      </IfmsModal>
    </div>
  );
}

export default AccountCode;
